import React, { useState } from 'react'
import { Grid, List } from 'lucide-react'
import TableViewer from './DataBrowser/TableViewer'
import CardViewer from './DataBrowser/CardViewer'
import FileViewer from './FileBrowser/FileViewer'
import RoleManagement from './RoleManagement'
import ConnectionStatus from './ConnectionStatus'

interface ContentAreaProps {
  currentView: 'dashboard' | 'data-browser' | 'file-browser' | 'role-management'
  selectedTable?: 'users' | 'books' | 'chapters' | 'api_usage'
  selectedFilePath?: string
}

const ContentArea: React.FC<ContentAreaProps> = ({
  currentView,
  selectedTable,
  selectedFilePath
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards')
  const getContentTitle = () => {
    if (currentView === 'dashboard') {
      return 'Dashboard'
    }
    
    if (currentView === 'data-browser' && selectedTable) {
      const tableNames = {
        users: 'Users',
        books: 'Books', 
        chapters: 'Chapters',
        api_usage: 'API Usage Logs'
      }
      return tableNames[selectedTable]
    }
    
    if (currentView === 'file-browser' && selectedFilePath) {
      const pathNames = {
        'covers/': 'Cover Images',
        'audio/': 'Audio Files',
        'misc/': 'Miscellaneous Files'
      }
      return pathNames[selectedFilePath as keyof typeof pathNames] || 'Files'
    }

    if (currentView === 'role-management') {
      return 'Role Management'
    }
    
    return 'Welcome'
  }

  const getContentSubtitle = () => {
    if (currentView === 'dashboard') {
      return 'System overview and connection status for your Voyager admin environment'
    }
    
    if (currentView === 'data-browser' && selectedTable) {
      return `Browse and manage ${selectedTable} data from the D1 database`
    }
    
    if (currentView === 'file-browser' && selectedFilePath) {
      return `Browse and manage files in R2 storage: ${selectedFilePath}`
    }

    if (currentView === 'role-management') {
      return 'Manage user roles and permissions (Super Admin access required)'
    }
    
    return 'Select an item from the sidebar to get started'
  }

  const renderViewToggle = () => {
    if (currentView !== 'data-browser' || !selectedTable) return null
    
    return (
      <div className="view-toggle">
        <button 
          className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
          onClick={() => setViewMode('cards')}
          title="Card View"
        >
          <Grid size={16} />
          Cards
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
          onClick={() => setViewMode('table')}
          title="Table View"
        >
          <List size={16} />
          Table
        </button>
      </div>
    )
  }

  const renderContent = () => {
    if (currentView === 'dashboard') {
      return <ConnectionStatus />
    }
    
    if (currentView === 'data-browser' && selectedTable) {
      return viewMode === 'cards' 
        ? <CardViewer tableName={selectedTable} />
        : <TableViewer tableName={selectedTable} />
    }
    
    if (currentView === 'file-browser' && selectedFilePath) {
      return <FileViewer path={selectedFilePath} />
    }

    if (currentView === 'role-management') {
      return <RoleManagement />
    }
    
    return (
      <div className="welcome-message">
        <h2 className="welcome-title">Voyager Admin Dashboard</h2>
        <p className="welcome-description">
          Use the sidebar to browse database tables, manage files in R2 storage, or manage user roles.
          This admin interface provides comprehensive access to your Voyager platform administration.
        </p>
      </div>
    )
  }

  return (
    <main className="content-area">
      <div className="content-header">
        <div className="header-left">
          <h1 className="content-title">{getContentTitle()}</h1>
          <p className="content-subtitle">{getContentSubtitle()}</p>
        </div>
        <div className="header-right">
          {renderViewToggle()}
        </div>
      </div>
      <div className="content-body">
        {renderContent()}
      </div>
    </main>
  )
}

export default ContentArea